import { useState, useEffect } from 'react';
import { useAuthGuard } from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import client from '../api/client';

const ITEMS = [
  { key: 'price_score', label: '가격 수준', tip: '가격이 높을수록 소비자가 더 많이 고민하고 비교합니다.', hint: '저가·충동구매 가능 ↔ 고가·신중 구매' },
  { key: 'frequency_score', label: '구매 빈도', tip: '자주 사는 제품은 습관적으로 구매하는 경향이 있습니다.', hint: '드문 구매·특별한 결정 (1) ↔ 매일·습관적 반복 구매 (10)' },
  { key: 'search_score', label: '정보 탐색', tip: '구매 전 정보 탐색이 많을수록 고관여입니다.', hint: '탐색 거의 없음 ↔ 탐색 많음' },
  { key: 'risk_score', label: '실패 리스크', tip: '잘못 샀을 때 손해가 클수록 소비자는 더 신중해집니다.', hint: '실패해도 부담 없음 ↔ 실패 시 부담 큼' },
  { key: 'identity_score', label: 'SNS·과시 가치', tip: '이 제품을 샀다고 SNS에 올리거나 남들에게 자랑하고 싶은가요?', hint: '아무도 관심 없는 소모품 (1) ↔ SNS 인증·과시하고 싶은 제품 (10)' },
  { key: 'brand_score', label: '브랜드 인지도', tip: '브랜드가 유명할수록 소비자는 고민 없이 삽니다. 무명 브랜드일수록 후기·비교를 더 많이 합니다.', hint: '무명 브랜드·직접 검증 필요 (1) ↔ 삼성·애플급 신뢰 브랜드 (10)' },
];

const PLACE_STRATEGY: Record<string, any> = {
  저관여: {
    color: '#00D4AA', bg: '#0F2A22', label: '🟢 저관여 플레이스',
    items: [
      { key: '노출 전략', val: '네이버 지도·플레이스 검색 상위 노출 (N1 키워드 최적화)' },
      { key: '콘텐츠 전략', val: '대표 사진 최적화·메뉴 등록·짧은 소개글 (임팩트 중심)' },
      { key: '리뷰 전략', val: '방문 후 즉시 리뷰 유도 QR코드·리뷰 이벤트' },
      { key: '구매 여정', val: '검색 → 지도 노출 → 즉시 방문 (짧은 여정)' },
      { key: '핵심 KPI', val: '플레이스 순위·방문자 리뷰수·저장수' },
    ]
  },
  중간관여: {
    color: '#F6AD55', bg: '#2A1F0D', label: '🟡 중간 관여 플레이스',
    items: [
      { key: '노출 전략', val: '키워드 다양화·블로그 체험단·소식 정기 업데이트' },
      { key: '콘텐츠 전략', val: '스토리 있는 소개글·메뉴 키워드 최적화·공지 활용' },
      { key: '리뷰 전략', val: '블로그 리뷰 확보·방문자 리뷰 이벤트 병행' },
      { key: '구매 여정', val: '검색 → 비교 → 블로그 확인 → 방문' },
      { key: '핵심 KPI', val: '블로그 리뷰수·예약수·플레이스 순위' },
    ]
  },
  고관여: {
    color: '#F56565', bg: '#2A0D0D', label: '🔴 고관여 플레이스',
    items: [
      { key: '노출 전략', val: '프리미엄 키워드 N1 최적화·네이버 예약 연동' },
      { key: '콘텐츠 전략', val: '전문성 강조 소개글·수상이력·인증·언론 보도 활용' },
      { key: '리뷰 전략', val: '고품질 블로그 리뷰·인플루언서 체험단·평점 관리' },
      { key: '구매 여정', val: '검색 → 블로그 탐색 → 플레이스 확인 → 예약 → 방문' },
      { key: '핵심 KPI', val: '예약 완료수·블로그 리뷰수·N1 순위·평점' },
    ]
  }
};

const STRATEGY: Record<string, any> = {
  저관여: {
    color: '#00D4AA', bg: '#0F2A22', label: '🟢 저관여 제품',
    items: [
      { key: '채널 전략', val: 'SNS 광고·인플루언서·충동구매 유도 콘텐츠 (노출 빈도 극대화)' },
      { key: '콘텐츠 전략', val: '짧고 임팩트 있는 비주얼·감성 소구 (Why Not 전략)' },
      { key: '가격·프로모션', val: '묶음 할인·한정판·즉시 쿠폰 (즉각 전환 유도)' },
      { key: '구매 여정', val: '인지 → 즉시구매 (짧은 퍼널)' },
      { key: '핵심 KPI', val: '노출수·클릭률·전환율' },
    ]
  },
  중간관여: {
    color: '#F6AD55', bg: '#2A1F0D', label: '🟡 중간 관여 제품',
    items: [
      { key: '채널 전략', val: '검색광고 + SNS 병행, 비교 콘텐츠' },
      { key: '콘텐츠 전략', val: '감성 + 이성 균형, 후기 + 스펙 병행' },
      { key: '가격·프로모션', val: '체험 이벤트·할인 + 후기 결합' },
      { key: '구매 여정', val: '인지 → 관심 → 비교 → 구매' },
      { key: '핵심 KPI', val: 'CTR·페이지 체류시간·리드수' },
    ]
  },
  고관여: {
    color: '#F56565', bg: '#2A0D0D', label: '🔴 고관여 제품',
    items: [
      { key: '채널 전략', val: '검색광고·유튜브 리뷰·블로그 심층 비교' },
      { key: '콘텐츠 전략', val: '상세 스펙·전문가 추천·리뷰·비교표' },
      { key: '가격·프로모션', val: '보증·AS 강조·프리미엄 포지셔닝' },
      { key: '구매 여정', val: '인지 → 탐색 → 비교 → 신뢰 → 구매' },
      { key: '핵심 KPI', val: '리뷰수·재구매율·NPS' },
    ]
  }
};

const REF_TABLE = [
  { key: '판정 기준', 저관여: '저가·반복구매·탐색 거의 없음·실패해도 부담 없음·정체성과 무관', 중간관여: '가격 중간·가끔 구매·어느 정도 비교함·리스크 보통·약간의 자아 표현', 고관여: '고가·드문 구매·비교·검색 많음·실패 시 손해 큼·자아 표현 강함' },
  { key: '소비자 심리', 저관여: '"그냥 사도 돼, 별로 안 비싸잖아"', 중간관여: '"좀 찾아보고 살까?"', 고관여: '"이거 정말 나한테 맞는지 충분히 알아봐야 해"' },
  { key: '제품 예시', 저관여: '과자·음료·생활소모품·저가 앱', 중간관여: '의류·중가 가전·운동용품', 고관여: '자동차·부동산·명품·B2B 솔루션·결혼 서비스' },
  { key: '채널 전략', 저관여: 'SNS 광고·인플루언서·쇼핑 피드 노출 극대화', 중간관여: '검색광고 + SNS 병행, 비교 콘텐츠', 고관여: '검색광고·유튜브 리뷰·블로그 심층 비교' },
  { key: '콘텐츠 전략', 저관여: '짧고 감성적인 비주얼, 충동 유발 카피', 중간관여: '감성 + 이성 균형, 후기 + 스펙 병행', 고관여: '상세 스펙·전문가 추천·리뷰·비교표' },
  { key: '가격·프로모션', 저관여: '묶음 할인·즉시 쿠폰·한정판', 중간관여: '체험 이벤트·할인 + 후기 결합', 고관여: '보증·AS 강조·프리미엄 포지셔닝' },
  { key: '구매 여정', 저관여: '인지 → 즉시 구매 (짧은 퍼널)', 중간관여: '인지 → 관심 → 비교 → 구매', 고관여: '인지 → 탐색 → 비교 → 신뢰 → 구매' },
  { key: '핵심 KPI', 저관여: '노출수·클릭률·즉시 전환율', 중간관여: 'CTR·페이지 체류시간·리드수', 고관여: '리뷰수·재구매율·NPS' },
  { key: '광고 메시지 톤', 저관여: '"지금 바로!", "오늘만!", "이 가격에?"', 중간관여: '"많은 분들이 선택했어요", "비교해보세요"', 고관여: '"당신의 선택이 맞습니다", "전문가가 인정한"' },
  { key: '신뢰 구축 방법', 저관여: '브랜드 친숙도·반복 노출', 중간관여: '사용자 후기·평점', 고관여: '수상 이력·인증·언론 보도·전문가 추천' },
];

export default function Involvement() {
  const checkAuth = useAuthGuard();
  const [title, setTitle] = useState('');
  const [storeUrl, setStoreUrl] = useState('');
  const [storeType, setStoreType] = useState('');
  const [scores, setScores] = useState<Record<string, number>>({ price_score: 5, frequency_score: 5, search_score: 5, risk_score: 5, identity_score: 5, brand_score: 5 });
  const [saved, setSaved] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [tab, setTab] = useState<'diagnose' | 'history'>('diagnose');

  const avg = Math.round((
    scores.price_score +
    (11 - scores.frequency_score) +
    scores.search_score +
    scores.risk_score +
    scores.identity_score +
    (11 - scores.brand_score)
  ) / 6);
  const level = avg <= 4 ? '저관여' : avg <= 6 ? '중간관여' : '고관여';
  const isPlace = storeType === '네이버 플레이스';
  const strategy = isPlace ? PLACE_STRATEGY[level] : STRATEGY[level];

  const loadHistory = async () => {
    try {
      const res = await client.get('/involvement/list');
      setHistory(res.data.results);
    } catch {}
  };

  useEffect(() => { loadHistory(); }, []);

  const handleSave = async () => {
    if (!checkAuth()) return;
    if (!title.trim()) return alert('진단 제목을 입력해주세요');
    try {
      await client.post('/involvement/save', { title, store_url: storeUrl, store_type: storeType, ...scores });
      setSaved(true);
      await loadHistory();
      setTimeout(() => setSaved(false), 2000);
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      if (status === 429) {
        if (window.confirm(detail + '\n\n업그레이드하시겠습니까?')) {
          window.location.href = '/pricing';
        }
      } else {
        alert('저장 실패');
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('삭제하면 연결된 마케팅 전략·키워드 전략·전략 리포트도 함께 삭제됩니다.\n계속하시겠습니까?')) return;
    try {
      await client.delete(`/involvement/${id}`);
      await loadHistory();
    } catch {}
  };

  const s: any = {
    card: { background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 20, marginBottom: 16 },
    input: { background: '#0F1117', border: '0.5px solid #2A3045', borderRadius: 8, padding: '10px 14px', color: '#E8ECF4', fontSize: 14, width: '100%', boxSizing: 'border-box' },
    btn: { background: '#00D4AA', border: 'none', borderRadius: 8, padding: '10px 28px', color: '#0A1F18', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
    btnGhost: { background: 'none', border: '0.5px solid #2A3045', borderRadius: 6, padding: '7px 16px', color: '#B0BAC8', fontSize: 13, cursor: 'pointer' },
    tab: (active: boolean) => ({ background: active ? '#1A2035' : 'none', border: active ? '0.5px solid #00D4AA' : '0.5px solid #2A3045', borderRadius: 6, padding: '7px 20px', color: active ? '#00D4AA' : '#8892A4', fontSize: 13, cursor: 'pointer', fontWeight: active ? 700 : 400 }),
  };

  return (
    <Layout>
      <div style={{ maxWidth: 900 }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ color: '#E8ECF4', fontSize: 18, fontWeight: 700 }}>관여도 진단</div>
          <div style={{ color: '#B0BAC8', fontSize: 13, marginTop: 4 }}>5개 항목 점수로 제품의 관여도를 진단하고 마케팅 전략을 확인합니다</div>
        </div>

        {/* 탭 */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <button style={s.tab(tab === 'diagnose')} onClick={() => setTab('diagnose')}>진단하기</button>
          <button style={s.tab(tab === 'history')} onClick={() => setTab('history')}>저장된 진단 ({history.length})</button>
        </div>

        {tab === 'diagnose' && (
          <>
            {/* 제목 입력 */}
            <div style={s.card}>
              <div style={{ color: '#B0BAC8', fontSize: 13, marginBottom: 6 }}>진단 제목 (제품명 또는 프로젝트명)</div>
              <input style={s.input} value={title} onChange={e => setTitle(e.target.value)} placeholder="예) 나이키 러닝화 또는 강남 스타벅스 xx점" />
              <div style={{ color: '#B0BAC8', fontSize: 12, marginTop: 14, marginBottom: 6 }}>URL (스마트스토어 상품 URL 또는 네이버 플레이스 URL)</div>
              <input style={{ ...s.input, marginBottom: 8 }} value={storeUrl} onChange={e => setStoreUrl(e.target.value)} placeholder="예) https://smartstore.naver.com/store/products/123456  또는  https://map.naver.com/p/entry/place/..." />
              <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                {['스마트스토어', '쿠팡', '11번가', 'G마켓', '자사몰', '네이버 플레이스'].map(ch => (
                  <button key={ch} onClick={() => setStoreType(ch)} style={{
                    background: storeType === ch ? '#1A2035' : 'none',
                    border: `1px solid ${storeType === ch ? '#00D4AA' : '#2A3045'}`,
                    borderRadius: 6, padding: '4px 12px',
                    color: storeType === ch ? '#00D4AA' : '#8892A4',
                    fontSize: 12, cursor: 'pointer'
                  }}>{ch}</button>
                ))}
              </div>
            </div>

            {/* 점수 입력 */}
            <div style={s.card}>
              <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 16 }}>5개 항목 점수 입력</div>
              {ITEMS.map(item => (
                <div key={item.key} style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <div>
                      <span style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600 }}>{item.label}</span>
                      <span style={{ color: '#B0BAC8', fontSize: 13, marginLeft: 8 }}>💡 {item.tip}</span>
                    </div>
                    <div style={{ background: '#0F1117', borderRadius: 6, padding: '4px 12px', color: '#00D4AA', fontWeight: 700, fontSize: 16, minWidth: 36, textAlign: 'center' }}>
                      {scores[item.key]}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>

                    <input type="range" min={1} max={10} value={scores[item.key]}
                      onChange={e => setScores(prev => ({ ...prev, [item.key]: Number(e.target.value) }))}
                      style={{ flex: 1, accentColor: '#00D4AA' }} />

                  </div>
                  <div style={{ color: '#8892A4', fontSize: 13, marginTop: 2 }}>{item.hint}</div>
                </div>
              ))}
            </div>

            {/* 결과 */}
            <div style={{ ...s.card, background: strategy.bg, border: `1px solid ${strategy.color}33` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                  <div style={{ color: '#B0BAC8', fontSize: 13, marginBottom: 4 }}>평균 점수</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ color: strategy.color, fontSize: 36, fontWeight: 700 }}>{avg}</span>
                    <span style={{ color: '#8892A4', fontSize: 13 }}>/ 10</span>
                  </div>
                </div>
                <div style={{ background: strategy.color + '22', border: `1px solid ${strategy.color}`, borderRadius: 8, padding: '8px 20px', color: strategy.color, fontSize: 16, fontWeight: 700 }}>
                  {strategy.label}
                </div>
              </div>

              <div style={{ color: '#B0BAC8', fontSize: 13, marginBottom: 10, fontWeight: 600 }}>마케팅 전략 힌트</div>
              {strategy.items.map((item: any) => (
                <div key={item.key} style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: '0.5px solid #2A3045' }}>
                  <span style={{ color: strategy.color, fontSize: 13, fontWeight: 600, width: 80, flexShrink: 0 }}>{item.key}</span>
                  <span style={{ color: '#E8ECF4', fontSize: 13 }}>{item.val}</span>
                </div>
              ))}
            </div>

            {/* 저장 */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 32 }}>
              <button style={s.btn} onClick={handleSave}>{saved ? '✓ 저장됨' : '저장하기'}</button>
            </div>

            {/* 참조 테이블 */}
            <div style={s.card}>
              <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 16 }}>관여도 기준 참조표</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ background: '#1A2035', color: '#8892A4', padding: '8px 12px', textAlign: 'left', borderBottom: '0.5px solid #2A3045', width: 100 }}>구분</th>
                      <th style={{ background: '#0F2A22', color: '#00D4AA', padding: '8px 12px', textAlign: 'left', borderBottom: '0.5px solid #2A3045' }}>🟢 저관여 (1~4점)</th>
                      <th style={{ background: '#2A1F0D', color: '#F6AD55', padding: '8px 12px', textAlign: 'left', borderBottom: '0.5px solid #2A3045' }}>🟡 중간 관여 (5~6점)</th>
                      <th style={{ background: '#2A0D0D', color: '#F56565', padding: '8px 12px', textAlign: 'left', borderBottom: '0.5px solid #2A3045' }}>🔴 고관여 (7~10점)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {REF_TABLE.map((row, i) => (
                      <tr key={row.key} style={{ background: i % 2 === 0 ? '#161B27' : '#0F1117' }}>
                        <td style={{ color: '#8892A4', padding: '8px 12px', fontWeight: 600, borderBottom: '0.5px solid #1E2535' }}>{row.key}</td>
                        <td style={{ color: '#E8ECF4', padding: '8px 12px', borderBottom: '0.5px solid #1E2535' }}>{row.저관여}</td>
                        <td style={{ color: '#E8ECF4', padding: '8px 12px', borderBottom: '0.5px solid #1E2535' }}>{row.중간관여}</td>
                        <td style={{ color: '#E8ECF4', padding: '8px 12px', borderBottom: '0.5px solid #1E2535' }}>{row.고관여}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {tab === 'history' && (
          <div>
            {history.length === 0 ? (
              <div style={{ ...s.card, textAlign: 'center', color: '#8892A4', padding: 40 }}>저장된 진단이 없습니다</div>
            ) : history.map(r => {
              const st = STRATEGY[r.level];
              return (
                <div key={r.id} style={s.card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{r.title}</div>
                      {r.store_url && <div style={{ color: '#4A5568', fontSize: 11, marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>{r.store_url}</div>}
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ background: st?.color + '22', border: `1px solid ${st?.color}55`, borderRadius: 6, padding: '3px 10px', color: st?.color, fontSize: 13, fontWeight: 600 }}>{st?.label}</span>
                        <span style={{ color: '#B0BAC8', fontSize: 13 }}>평균 {r.avg_score}점</span>
                        <span style={{ color: '#B0BAC8', fontSize: 13 }}>{new Date(r.created_at).toLocaleDateString('ko-KR')}</span>
                      </div>
                    </div>
                    <button style={s.btnGhost} onClick={() => handleDelete(r.id)}>삭제</button>
                  </div>
                  <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
                    {ITEMS.map(item => (
                      <div key={item.key} style={{ textAlign: 'center' }}>
                        <div style={{ color: '#8892A4', fontSize: 10 }}>{item.label}</div>
                        <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600 }}>{r[item.key]}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
